import api from '../services/api';
import {
  ProductionBatch,
  RepairItem,
  RepairType,
  CreateOpeningEntryDTO,
  QuickOpeningEntryDTO,
  ProcessStageDTO,
  DashboardStats
} from '../types/production';

// Export press API
export { pressApi } from './press';

export const productionApi = {
  // Get all batches
  getAll: async (params?: { status?: string; current_stage?: string; design_no?: string }) => {
    const response = await api.get('/production', { params });
    return response.data as ProductionBatch[];
  },

  // Get single batch
  getById: async (id: number) => {
    const response = await api.get(`/production/${id}`);
    return response.data as ProductionBatch;
  },

  // Create opening entry
  createOpeningEntry: async (data: CreateOpeningEntryDTO) => {
    const response = await api.post('/production/opening-entry', data);
    return response.data;
  },

  // Bulk opening entry from CSV
  bulkOpeningEntry: async (file: File, repairTypeId?: number) => {
    const formData = new FormData();
    formData.append('csv_file', file);
    if (repairTypeId) {
      formData.append('repair_type_id', repairTypeId.toString());
    }
    const response = await api.post('/production/opening-entry/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Compare ERP CSV data with system data
  compareWithERP: async (file: File) => {
    const formData = new FormData();
    formData.append('csv_file', file);
    const response = await api.post('/production/opening-entry/compare', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Create quick opening entry
  createQuickOpeningEntry: async (data: QuickOpeningEntryDTO) => {
    const response = await api.post('/production/opening-entry/quick', data);
    return response.data;
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    const response = await api.get('/production/stats/dashboard');
    return response.data as DashboardStats;
  },

  // Get design-wise stats
  getDesignWiseStats: async (status?: string) => {
    const response = await api.get('/production/stats/by-design', { params: { status } });
    return response.data;
  },

  // Get pending to receive items
  getPendingToReceive: async (status?: 'ISSUED' | 'COMPLETED' | 'BOTH') => {
    const response = await api.get('/production/pending-to-receive', { params: { status } });
    return response.data;
  },

  // Receive from stitching
  receiveFromStitching: async (data: { job_work_id: number; sizes: { [size: string]: number }; garment_part: string }) => {
    const response = await api.post('/production/receive-from-stitching', data);
    return response.data;
  },

  // Receive at stitching unit
  receiveAtStitching: async (data: { job_work_id: number; challan_date: string }) => {
    const response = await api.post('/production/receive-at-stitching', data);
    return response.data;
  },

  // Complete stitching
  completeStitching: async (data: { design_cutting_id: number; stitched_pieces: { [size: string]: number } }) => {
    const response = await api.post('/production/complete-stitching', data);
    return response.data;
  },

  // Get finishing dashboard data (design-wise stage breakdown with images & aging)
  getFinishingDashboard: async () => {
    const response = await api.get('/production/stats/finishing-dashboard');
    return response.data;
  }
};

export const repairsApi = {
  // Get repair types
  getTypes: async () => {
    const response = await api.get('/repairs/types');
    return response.data as RepairType[];
  },

  // Create repair type
  createType: async (data: { name: string; category: string; description?: string }) => {
    const response = await api.post('/repairs/types', data);
    return response.data as RepairType;
  },

  // Update repair type
  updateType: async (id: number, data: Partial<RepairType>) => {
    const response = await api.put(`/repairs/types/${id}`, data);
    return response.data;
  },

  // Delete repair type
  deleteType: async (id: number) => {
    const response = await api.delete(`/repairs/types/${id}`);
    return response.data;
  },

  // Get repair queue
  getQueue: async (params?: { status?: string; category?: string; assigned_to?: string }) => {
    const response = await api.get('/repairs/queue', { params });
    return response.data as RepairItem[];
  },

  // Create repair item
  createRepairItem: async (data: any) => {
    const response = await api.post('/repairs/queue', data);
    return response.data as RepairItem;
  },

  // Assign repair
  assignRepair: async (id: number, assigned_to: string, quantity?: number) => {
    const response = await api.put(`/repairs/queue/${id}/assign`, { assigned_to, quantity });
    return response.data as RepairItem;
  },

  // Complete repair
  completeRepair: async (id: number, data: { repaired_qty: number; permanently_damaged_qty: number; notes?: string; return_to_stage?: string }) => {
    const response = await api.put(`/repairs/queue/${id}/complete`, data);
    return response.data;
  },

  // Barcode repair items
  barcodeRepair: async (id: number, data: { barcoded_qty: number; permanent_damage_qty: number }) => {
    const response = await api.put(`/repairs/queue/${id}/barcode`, data);
    return response.data;
  },

  // Edit repair item (MASTER only)
  editRepairItem: async (id: number, data: { quantity?: number; assigned_to?: string; damage_notes?: string }) => {
    const response = await api.put(`/repairs/queue/${id}/edit`, data);
    return response.data;
  },

  // Delete repair item (MASTER only)
  deleteRepairItem: async (id: number) => {
    const response = await api.delete(`/repairs/queue/${id}`);
    return response.data;
  }
};

export const finishingApi = {
  // Get pending QC designs (design level)
  getPendingQcDesigns: async () => {
    const response = await api.get('/finishing/pending-qc');
    return response.data;
  },

  // Rectify QC — mirror TOP barcoded/damaged to other pending parts (MASTER only)
  rectifyQc: async () => {
    const response = await api.post('/finishing/rectify-qc');
    return response.data;
  },

  // Process QC — per garment part
  processQc: async (data: {
    design_no: string;
    parts: Array<{
      garment_part: string;
      good_qty: number;
      damage_qty: number;
      permanent_damage_qty: number;
    }>;
    repairs?: Array<{ garment_part?: string; size?: string; qty: number; damage_type_id: number; notes?: string }>;
    notes?: string;
    include_sample?: boolean;
  }) => {
    const response = await api.post('/finishing/process-qc', data);
    return response.data;
  },

  // Get complete designs
  getCompleteDesigns: async (search?: string) => {
    const response = await api.get('/finishing/complete-designs', {
      params: search ? { search } : {}
    });
    return response.data;
  },

  // Get design breakdown
  getDesignBreakdown: async (designNo: string) => {
    const response = await api.get(`/finishing/complete-designs/${encodeURIComponent(designNo)}/breakdown`);
    return response.data;
  },

  // Edit batch size breakdown quantities (MASTER only)
  editBatchBreakdown: async (batchId: number, data: { barcoded_qty?: number; damaged_qty?: number; qc_qty?: number; in_repair_qty?: number }) => {
    const response = await api.put(`/finishing/batch-breakdown/${batchId}`, data);
    return response.data;
  },

  // Edit a QC stage record (MASTER only)
  editQcRecord: async (recordId: number, data: { good_qty: number; damage_qty: number; permanent_damage_qty: number }) => {
    const response = await api.put(`/finishing/qc-record/${recordId}`, data);
    return response.data;
  },

  // Delete a QC stage record (MASTER only)
  deleteQcRecord: async (recordId: number) => {
    const response = await api.delete(`/finishing/qc-record/${recordId}`);
    return response.data;
  }
};

